/** Похожие модели карточки серии — ровно то, что отдают `GET /api/serials/{serial}/similar`
 *  (по векторам фото, кэш) и `GET /api/serials/{serial}/models` (связи CRM); наличие — по правилам
 *  «региона матрицы» CRM. Спека `docs/specs/2026-10-22-serial-models-design.md`. */
import { apiFetch } from "@/lib/api";

export type ModelLink = "self" | "same" | "group" | "similar" | "photo";

export type ModelCity = { id: number; short: string; total: number; transit: number; sizes: { size: string; n: number }[] };

export type ModelSerial = { serial: number; size: string; place: string | null; next_deal: number | null; transit: boolean };

export type ModelCard = {
  model_id: number;
  name: string;
  link: ModelLink;
  /** Сходство фото 0…1 — только у похожих по фото. */
  score: number | null;
  photo_url: string | null;
  total: number;
  cities: ModelCity[];
  /** Свободные серии выбранного салона. */
  serials: ModelSerial[];
  /** Свободные серии прочих городов — отметить можно и модель, которой в салоне нет. */
  others: (ModelSerial & { location_id: number; city: string })[];
};

export type SerialModels = {
  serial: number | null;
  model_id: number;
  location_id: number | null;
  locations: { id: number; short: string }[];
  models: ModelCard[];
  /** Моделей без наличия — не показаны. */
  hidden: number;
  computed_date?: string | null;
  error?: string;
};

export type ModelsTab = "similar" | "models";

/** От чего искать: серия (карточка серии) или модель без серии («понравилось» вкладки «Товар»). */
export type ModelsOf = { serial: number } | { model: number };

const base = (of: ModelsOf) => ("serial" in of ? `/api/serials/${of.serial}` : `/api/dress/model/${of.model}`);
const at = (location: number | null) => (location ? `?location=${location}` : "");

export const serialModelsQuery = (of: ModelsOf, tab: ModelsTab, location: number | null) => ({
  queryKey: ["serial-models", "serial" in of ? `s${of.serial}` : `m${of.model}`, tab, location],
  queryFn: () => apiFetch<SerialModels>(`${base(of)}/${tab}${at(location)}`),
  staleTime: 5 * 60_000,
});

export const refreshSimilar = (of: ModelsOf, location: number | null) =>
  apiFetch<SerialModels>(`${base(of)}/similar/refresh${at(location)}`, { method: "POST" });

export const LINK_LABEL: Record<ModelLink, string> = {
  self: "эта модель",
  same: "одинаковая",
  group: "склейка",
  similar: "похожая в CRM",
  photo: "по фото",
};

/** Фото с номером серии внизу — файлом для поля ответа; имя видит клиент: `<серия>.jpg`. */
export async function stampSerial(photoUrl: string, serial: number): Promise<File> {
  const r = await fetch(photoUrl, { credentials: "include" });
  if (!r.ok) throw new Error(`Фото серии ${serial} не загрузилось`);
  const bitmap = await createImageBitmap(await r.blob());
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bitmap, 0, 0);
  const font = Math.max(18, Math.round(bitmap.width / 16));
  const bar = Math.round(font * 1.7);
  ctx.fillStyle = "rgba(0,0,0,.62)";
  ctx.fillRect(0, bitmap.height - bar, bitmap.width, bar);
  ctx.fillStyle = "#fff";
  ctx.font = `600 ${font}px system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(`Серия ${serial}`, bitmap.width / 2, bitmap.height - bar / 2);
  const blob = await new Promise<Blob>((ok, fail) => canvas.toBlob((b) => (b ? ok(b) : fail(new Error("canvas"))), "image/jpeg", 0.9));
  return new File([blob], `${serial}.jpg`, { type: "image/jpeg" });
}
